import { useState, useEffect } from 'react';
import { FaCookieBite, FaTimes, FaCog, FaCheck, FaChartLine, FaBullhorn } from 'react-icons/fa';
import styles from './CookieBanner.module.css';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Siempre activadas
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Verificar si ya se han aceptado las cookies
    const cookiesAccepted = localStorage.getItem('cookiesAccepted');
    if (!cookiesAccepted) {
      setIsVisible(true);
    } else {
      // Cargar preferencias guardadas
      const savedPreferences = localStorage.getItem('cookiePreferences');
      if (savedPreferences) {
        setCookiePreferences(JSON.parse(savedPreferences));
      }
    }
  }, []);

  const acceptAllCookies = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const acceptSelectedCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setIsVisible(false);
  };

  const rejectOptionalCookies = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    setIsVisible(false);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return; // No se puede desactivar
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className={styles.cookieBanner}>
      <div className={styles.bannerContent}>
        <div className={styles.bannerHeader}>
          <div className={styles.iconContainer}>
            <FaCookieBite className={styles.cookieIcon} />
          </div>
          <div className={styles.headerText}>
            <h3>Configuración de Cookies</h3>
            <p>Utilizamos cookies para mejorar tu experiencia en nuestro sitio web</p>
          </div>
          <button 
            className={styles.closeButton}
            onClick={() => setIsVisible(false)}
            aria-label="Cerrar banner"
          >
            <FaTimes />
          </button>
        </div>

        {!showSettings ? (
          <div className={styles.simpleView}>
            <p className={styles.description}>
              Usamos cookies esenciales para el funcionamiento del sitio y cookies opcionales 
              para análisis y marketing. Puedes gestionar tus preferencias a continuación.
            </p>
            
            <div className={styles.buttonGroup}>
              <button 
                className={styles.settingsButton}
                onClick={() => setShowSettings(true)}
              >
                <FaCog /> Configurar
              </button>
              <button 
                className={styles.rejectButton}
                onClick={rejectOptionalCookies}
              >
                Solo Esenciales
              </button>
              <button 
                className={styles.acceptButton}
                onClick={acceptAllCookies}
              >
                <FaCheck /> Aceptar Todas
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.detailedView}>
            <div className={styles.cookieTypes}>
              <div className={styles.cookieType}>
                <div className={styles.typeHeader}>
                  <div className={styles.typeIcon}>
                    <FaCheck className={styles.necessaryIcon} />
                  </div>
                  <div className={styles.typeInfo}>
                    <h4>Cookies Esenciales</h4>
                    <p>Necesarias para el funcionamiento básico del sitio web</p>
                  </div>
                  <div className={styles.toggle}>
                    <input 
                      type="checkbox" 
                      checked={true} 
                      disabled 
                      className={styles.toggleInput}
                    />
                    <span className={`${styles.toggleSlider} ${styles.disabled}`}></span>
                  </div>
                </div>
              </div>

              <div className={styles.cookieType}>
                <div className={styles.typeHeader}>
                  <div className={styles.typeIcon}>
                    <FaChartLine className={styles.analyticsIcon} />
                  </div>
                  <div className={styles.typeInfo}>
                    <h4>Cookies Analíticas</h4>
                    <p>Recopilan datos sobre el comportamiento del usuario para mejorar el sitio</p>
                  </div>
                  <div className={styles.toggle}>
                    <input 
                      type="checkbox" 
                      checked={cookiePreferences.analytics}
                      onChange={() => handlePreferenceChange('analytics')}
                      className={styles.toggleInput}
                    />
                    <span 
                      className={`${styles.toggleSlider} ${cookiePreferences.analytics ? styles.active : ''}`}
                      onClick={() => handlePreferenceChange('analytics')}
                    ></span>
                  </div>
                </div>
              </div>

              <div className={styles.cookieType}>
                <div className={styles.typeHeader}>
                  <div className={styles.typeIcon}>
                    <FaBullhorn className={styles.marketingIcon} />
                  </div>
                  <div className={styles.typeInfo}>
                    <h4>Cookies de Marketing</h4>
                    <p>Rastrean al usuario para mostrarle publicidad personalizada</p>
                  </div>
                  <div className={styles.toggle}>
                    <input 
                      type="checkbox" 
                      checked={cookiePreferences.marketing}
                      onChange={() => handlePreferenceChange('marketing')}
                      className={styles.toggleInput}
                    />
                    <span 
                      className={`${styles.toggleSlider} ${cookiePreferences.marketing ? styles.active : ''}`}
                      onClick={() => handlePreferenceChange('marketing')}
                    ></span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.detailedButtons}>
              <button 
                className={styles.backButton}
                onClick={() => setShowSettings(false)}
              >
                Volver
              </button>
              <button 
                className={styles.saveButton}
                onClick={acceptSelectedCookies}
              >
                <FaCheck /> Guardar Preferencias
              </button>
            </div>

            <div className={styles.privacyLink}>
              <p>
                Para más información, consulta nuestra{' '}
                <a href="/privacidad" className={styles.link}>
                  Política de Privacidad
                </a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;